import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useCallback, useState } from "react";

import { useAppDispatch } from "app/hooks";
import {
  setActionType,
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import AppointmentOutcomeDialog from "components/appointment-outcome-dialog";
import { APPOINTMENT_STATUS_COMPLETED } from "constants/appointments";
import { CHART_NOTE_COMPLETED } from "constants/chart-notes";
import { NEW_APPOINTMENT_OUTCOME_CHANGES } from "constants/feature-flags";
import { useUpdateAppointmentMutation } from "services/appointments";
import { useUpdateChartNoteMutation } from "services/chart-notes";
import { eventsApi } from "services/events";
import { Diagnosis } from "services/types/diagnosis.type";

import { AppointmentOutcomeForm } from "../components/appointment-outcome-dialog/appointment-outcome-dialog.component";
import ErrorSnackbarContent from "../components/error-snackbar-content";

type SelectAppointmentOutcomeProviderProps = {
  children?: React.ReactNode;
};

const SelectAppointmentOutcomeContext = React.createContext<any>(null);

export function SelectAppointmentOutcomeProvider({
  children
}: SelectAppointmentOutcomeProviderProps) {
  const flags = useFlags();
  const newAppointmentOutcomeChangesEnabled =
    flags[NEW_APPOINTMENT_OUTCOME_CHANGES];
  const dispatch = useAppDispatch();
  const [appointmentOutcomeDialogOpen, setAppointmentOutcomeDialogOpen] =
    React.useState(false);
  const [currentAppointmentId, setCurrentAppointmentId] = useState<
    number | null
  >(null);
  const [postUpdateAction, setPostUpdateAction] = useState<() => void>(
    () => {}
  );
  const [onCancelAction, setOnCancelAction] = useState<() => void>(() => {});
  const [appointmentStatus, setAppointmentStatus] = useState<number>(
    APPOINTMENT_STATUS_COMPLETED
  );

  const [updateAppointment, { isLoading: isUpdatingOutcome }] =
    useUpdateAppointmentMutation();
  const [updateNote] = useUpdateChartNoteMutation();

  const initAppointmentOutcomeDialog = useCallback(
    (
      appointmentId: number,
      postUpdate: () => void,
      onCancel: () => void,
      status: number = APPOINTMENT_STATUS_COMPLETED
    ) => {
      setCurrentAppointmentId(appointmentId);
      setAppointmentOutcomeDialogOpen(true);
      setPostUpdateAction(() => postUpdate);
      setOnCancelAction(() => onCancel);
      setAppointmentStatus(status);
    },
    []
  );

  const submitOutcomeReason = useCallback(
    async ({
      chart_note_id,
      outcome_reason,
      outcome,
      outcome_notes,
      hearing_test_conducted,
      diagnosis,
      telehealth_provider,
      companion_present,
      hearing_aid_demo_performed,
      companion_type,
      companion_name
    }: Partial<AppointmentOutcomeForm>) => {
      const appointmentWithOutcome = {
        id: Number(currentAppointmentId),
        outcome: Number(outcome) || undefined,
        outcome_reason: Number(outcome_reason)
          ? { id: Number(outcome_reason) }
          : undefined,
        outcome_notes: newAppointmentOutcomeChangesEnabled
          ? undefined
          : outcome_notes,
        status: appointmentStatus,
        hearing_test_conducted: Boolean(hearing_test_conducted),
        diagnosis: diagnosis as Partial<Diagnosis>,
        telehealth_provider,
        companion_present,
        hearing_aid_demo_performed,
        companionType: companion_present ? companion_type || null : null,
        companionName: companion_present ? companion_name || "" : ""
      };
      if (newAppointmentOutcomeChangesEnabled && chart_note_id) {
        const appointmentNote = {
          id: chart_note_id,
          text: outcome_notes || undefined,
          icd10_codes: (diagnosis?.icd10_codes || []) as unknown as number[],
          status: CHART_NOTE_COMPLETED
        };
        try {
          const resp = await updateNote(appointmentNote);
          if ("error" in resp) {
            dispatch(
              setCustomMessage(<ErrorSnackbarContent response={resp} />)
            );
            dispatch(setOpen(true));
            return;
          }
        } catch (err) {
          dispatch(setCustomMessage(<ErrorSnackbarContent response={err} />));
          dispatch(setOpen(true));
          return;
        }
      }
      try {
        await updateAppointment(appointmentWithOutcome).unwrap();
        dispatch(eventsApi.util.invalidateTags(["Events"]));
        if (postUpdateAction) {
          postUpdateAction();
        } else {
          dispatch(setActionType("update"));
          dispatch(setOpen(true));
        }
        setCurrentAppointmentId(null);
        setAppointmentOutcomeDialogOpen(false);
      } catch (err) {
        dispatch(setCustomMessage(<ErrorSnackbarContent response={err} />));
        dispatch(setOpen(true));
      }
    },
    [
      updateAppointment,
      currentAppointmentId,
      dispatch,
      postUpdateAction,
      newAppointmentOutcomeChangesEnabled,
      updateNote,
      appointmentStatus
    ]
  );

  return (
    <SelectAppointmentOutcomeContext.Provider
      value={{
        appointmentOutcomeDialogOpen,
        setAppointmentOutcomeDialogOpen,
        initAppointmentOutcomeDialog,
        currentAppointmentId,
        setCurrentAppointmentId,
        submitOutcomeReason,
        isUpdatingOutcome,
        onCancelAction,
        appointmentStatus
      }}
    >
      {children}

      {appointmentOutcomeDialogOpen && <AppointmentOutcomeDialog />}
    </SelectAppointmentOutcomeContext.Provider>
  );
}

export default function useAppointmentOutcome() {
  const appointmentOutcomeContext = React.useContext(
    SelectAppointmentOutcomeContext
  );
  if (!appointmentOutcomeContext) {
    throw new Error(
      `useAppointmentOutcome must be used within a SelectAppointmentOutcomeProvider`
    );
  }

  return appointmentOutcomeContext;
}
